import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { usePermissions } from "@/hooks/usePermissions";

interface IPrivateRouteProps {
  children: ReactNode;
  /**
   * Permissions required to access this route.
   * By default, user must have ANY of these permissions (OR condition).
   */
  permissions?: string[];
  /**
   * If true, user must have ALL permissions. If false (default), user needs ANY permission.
   */
  requireAll?: boolean;
  /**
   * Path to redirect to when unauthorized. Defaults to "/unauthorized".
   */
  redirectTo?: string;
}

/**
 * Route-level permission guard.
 * Wraps route content and checks if user has required permissions.
 * Redirects to unauthorized page if access is denied.
 *
 * @example
 * // Require any of the listed permissions
 * <PrivateRoute permissions={["Travel:Reservations:Read", "Travel:Reservations:Write"]}>
 *   <ReservationListPage />
 * </PrivateRoute>
 *
 * @example
 * // Require ALL permissions
 * <PrivateRoute permissions={["Travel:Settings:Read", "Travel:Settings:Users"]} requireAll>
 *   <UsersSettingsPage />
 * </PrivateRoute>
 */
export function PrivateRoute({
  children,
  permissions = [],
  requireAll = false,
  redirectTo = "/unauthorized",
}: IPrivateRouteProps) {
  const location = useLocation();
  const { isLoading, hasNoPermissions, hasAllPermissions, hasAnyPermission } = usePermissions();

  // While loading auth state, don't render anything to avoid flash
  if (isLoading) {
    return null;
  }

  // If no permissions are specified, allow access (just auth check from AuthGuard)
  if (permissions.length === 0) {
    return <>{children}</>;
  }

  // Check permissions
  const hasAccess = requireAll
    ? hasAllPermissions(permissions)
    : hasAnyPermission(permissions);

  // Nothing granted at all - surface the dedicated "no access" reason
  if (hasNoPermissions) {
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location, reason: "no_permissions" }}
        replace
      />
    );
  }

  if (!hasAccess) {
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location, requiredPermissions: permissions }}
        replace
      />
    );
  }

  return <>{children}</>;
}
